/**
 * Shared types for iFrame bridge communication
 *
 * These types define the message protocol between the parent app
 * and the Compass web app when embedded as an iframe.
 */

/**
 * All supported iFrame message types
 */
export type IFrameMessageType =
  | 'AUTH_TOKEN'
  | 'REQUEST_TOKEN'
  | 'THEME'
  | 'NAVIGATE'
  | 'NAVIGATION_READY'
  | 'CLOSE_COMPASS';

/**
 * Generic iFrame message structure
 */
export interface IFrameMessage<T = unknown> {
  type: IFrameMessageType;
  payload?: T;
}

/**
 * Auth token payload from parent
 */
export interface AuthTokenPayload {
  token: string;
  expiresAt?: number; // Unix timestamp in milliseconds
}

/**
 * Theme payload from parent
 */
export interface ThemePayload {
  mode: 'light' | 'dark' | 'system';
}

/**
 * Navigation payload from parent
 */
export interface NavigatePayload {
  path: string;
  params?: Record<string, string>;
}

/**
 * Check if the app is running embedded in an iframe
 *
 * @returns true if embedded, false if running standalone
 */
export const isEmbedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Blocked by CORS means we're in an iframe
    return true;
  }
};

/**
 * Send a message to the parent window
 *
 * @param message - The message to send
 * @param targetOrigin - Target origin for security (defaults to '*' for development)
 */
export const sendToParent = <T>(
  message: IFrameMessage<T>,
  targetOrigin: string = '*'
): void => {
  if (!isEmbedded()) {
    console.debug('[iframe-bridge] Not embedded, skipping sendToParent:', message.type);
    return;
  }

  try {
    window.parent.postMessage(message, targetOrigin);
  } catch (error) {
    console.error('[iframe-bridge] Failed to send message to parent:', error);
  }
};
